import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { logErrors } from 'utils';
import { getFromBrandConfig } from 'brand-config';
import { selectAccessToken } from 'services/session/sessionSelectors';
import { getUserIdFromAccessToken } from 'services/session/utils';
import { fetchUserDetails } from 'services/user/userAsync';
import { selectUserId } from 'services/user/userSelectors';
import {
  selectOfferPlanCode,
  selectOfferGroupCode,
  selectIsOfferDetailsExist,
} from 'services/offer/offerSelectors';
import { getOfferDetails } from 'services/offer/offerAsync';
import {
  selectIsConversionEligibilityDetailsExist,
  selectIsBillingAccountDetailsExist,
  selectIsAvailableOffersFetched,
  selectIsEligibilityDetailsExist,
} from 'services/ctp/ctpSelectors';
import {
  fetchConversionEligibility,
  fetchAvailableOffers,
  fetchBillingAccountInfo,
  fetchEligibility,
} from 'services/ctp/ctpAsync';
import {
  selectIsCompanyDetailsExist,
  selectBrandVaultProject,
} from 'services/brand/brandSelectors';
import { fetchCompanyCodeDetails } from 'services/brand/brandAsync';
import { findPlanDetails, getUserPlan } from 'services/plan/planAsync';
import { selectEntitlementsLoaded } from 'services/plan/planSelectors';
import { initWorker } from 'services/vault/sdkFunctions';
import { selectHasVaultAccess } from 'services/vault/vaultSelectors';

function useSessionInitialSetup() {
  const dispatch = useAppDispatch();

  const userId = useAppSelector(selectUserId);
  const userAccessToken = useAppSelector(selectAccessToken);
  const vaultProject = useAppSelector(selectBrandVaultProject);
  const hasVaultAccess = useAppSelector(selectHasVaultAccess);
  const isOfferDetailsExist = useAppSelector(selectIsOfferDetailsExist);
  const isConversionEligibilityDetailsExist = useAppSelector(
    selectIsConversionEligibilityDetailsExist,
  );
  const isBillingAccountDetailsExist = useAppSelector(
    selectIsBillingAccountDetailsExist,
  );
  const isEligibilityDetailsExist = useAppSelector(
    selectIsEligibilityDetailsExist,
  );
  const isAvailableOffersFetched = useAppSelector(
    selectIsAvailableOffersFetched,
  );
  const offerPlanCode = useAppSelector(selectOfferPlanCode);
  const groupCode = useAppSelector(selectOfferGroupCode);
  const isCompanyDetailsExist = useAppSelector(selectIsCompanyDetailsExist);
  const entitlementsLoaded = useAppSelector(selectEntitlementsLoaded);

  const hasCustomerServiceSupport = getFromBrandConfig(
    'hasCustomerServiceSupport',
  );

  /* Fetch user details if not exist.
   *
   * Ref guard dedupes `GET /api/client/users/{id}` across React 18
   * StrictMode's mount → cleanup → mount cycle in dev (and any
   * other re-render that lands before the first fetch resolves).
   * The `!userId` slice-level gate is still correct, but `userId`
   * only flips once the response lands in Redux — so without the
   * ref the second mount sees the same empty `userId` and fires a
   * duplicate request. Keyed on the access token so a token
   * rotation (post-refresh / re-login) still re-fetches. */
  const userDetailsRequestedForTokenRef = useRef<string | null>(null);
  useEffect(() => {
    async function getUserDetails(token: string) {
      try {
        const identifier = getUserIdFromAccessToken(token);
        await dispatch(fetchUserDetails(identifier));
      } catch (error) {
        logErrors(error);
      }
    }

    if (!userAccessToken || userId) return;
    if (userDetailsRequestedForTokenRef.current === userAccessToken) return;
    userDetailsRequestedForTokenRef.current = userAccessToken;
    getUserDetails(userAccessToken);
  }, [userAccessToken, userId]);

  /* Fetch user offer details if not exist */
  useEffect(() => {
    async function getUserOfferDetails() {
      try {
        await dispatch(getOfferDetails());
      } catch (error) {
        logErrors(error);
      }
    }
    if (!isOfferDetailsExist && userId) {
      getUserOfferDetails();
    }
  }, [userId, isOfferDetailsExist]);

  /* Fetch user conversion elgibilty details if not exist */
  useEffect(() => {
    async function getConversionEligibility() {
      try {
        await dispatch(fetchConversionEligibility());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isConversionEligibilityDetailsExist && userId) {
      getConversionEligibility();
    }
  }, [userId, isConversionEligibilityDetailsExist]);

  /* Fetch user billing account details if not exist */
  useEffect(() => {
    async function getUserBillingAccountDetails() {
      try {
        await dispatch(fetchBillingAccountInfo());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isBillingAccountDetailsExist && userId) {
      getUserBillingAccountDetails();
    }
  }, [userId, isBillingAccountDetailsExist]);

  /* Fetch product-change eligibility if not exist (drives WGS upsell banner) */
  useEffect(() => {
    async function getEligibility() {
      try {
        await dispatch(fetchEligibility());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isEligibilityDetailsExist && userId) {
      getEligibility();
    }
  }, [userId, isEligibilityDetailsExist]);

  /* Fetch available offer details if not exist */
  useEffect(() => {
    async function getAvailableOffers() {
      try {
        await dispatch(fetchAvailableOffers());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isAvailableOffersFetched && userId) {
      getAvailableOffers();
    }
  }, [userId, isAvailableOffersFetched]);

  /* Fetch the user company details if not exist */
  useEffect(() => {
    async function getCompanyDetails(companyCode: string) {
      try {
        await dispatch(fetchCompanyCodeDetails(companyCode));
      } catch (error) {
        logErrors(error);
      }
    }

    if (hasCustomerServiceSupport && !isCompanyDetailsExist && groupCode) {
      getCompanyDetails(groupCode);
    }
  }, [groupCode, isCompanyDetailsExist, hasCustomerServiceSupport]);

  /* Fetch the user plan details if not exist */
  useEffect(() => {
    async function getPlanDetails(planCode: string) {
      try {
        await dispatch(findPlanDetails(planCode));
      } catch (error) {
        logErrors(error);
      }
    }

    if (!entitlementsLoaded && offerPlanCode) {
      getPlanDetails(offerPlanCode);
    }
  }, [offerPlanCode, entitlementsLoaded]);

  /* Angular parity — bootstrap fetch for `GET /api/client/plans/`
   * (`UserPlanService.USER_PLAN_URL` at
   * `apps/igww/src/app/user/user-plan.service.ts:19`). The response
   * carries the `features` array Angular scans at
   * `user-plan.service.ts:91-96` to derive `dark_web_child` and
   * `ssn_trace` child-feature entitlements. React stores that array
   * at `plan.rawPlanFeatures` and reads from it via the child-feature
   * selectors (`selectHasDarkWebChildPlanFeature`,
   * `selectHasSsnTraceChildPlanFeature`). Fired once per session, as
   * soon as we know the user is signed in — the data drives global
   * entitlement gates that any surface may hit. */
  useEffect(() => {
    async function fetchPlanFeatures() {
      try {
        await dispatch(getUserPlan());
      } catch (error) {
        logErrors(error);
      }
    }

    if (userId) {
      fetchPlanFeatures();
    }
  }, [userId]);

  // (Dashboard summary fetch removed — DashboardSummaryContext now owns it
  // and shares the single result between the sidebar icon and the alerts
  // card. See src/contexts/DashboardSummaryContext.tsx.)

  /* Init vault worker */
  useEffect(() => {
    const terminate =
      hasVaultAccess && vaultProject && userId
        ? initWorker({ project: vaultProject, userId })
        : () => null;

    return terminate;
  }, [hasVaultAccess, vaultProject, userId]);
}

export default useSessionInitialSetup;
